import { useMemo } from "react";
import BigNumber from "bignumber.js";
import PercentageInput from "../components/PercentageInput";
import PercentageChangeInput from "../components/PercentageChangeInput";
import CurrencyInput from "../components/CurrencyInput";
import AgeInput from "../components/AgeInput";
import { SectionHeader } from "../components/SectionHeader";
import { ShowMoreToggle } from "../components/ShowMoreToggle";
import { WithdrawalOrderSelect } from "../components/WithdrawalOrderSelect";
import { FeatureLockedContent } from "../components/FeatureGates";
import { UpgradePrompt } from "../components/UpgradePrompt";
import { useAssumptionsLogic } from "./Assumptions.logic";
import { useFieldStatistic } from "../hooks/useStatistics";

/** Portfolio allocation preset values */
const PRESETS = {
  conservative: { cash: 0.15, fixedIncome: 0.5, equities: 0.35 },
  moderate: { cash: 0.05, fixedIncome: 0.35, equities: 0.6 },
  aggressive: { cash: 0.02, fixedIncome: 0.18, equities: 0.8 },
} as const;

/**
 * AssumptionsForm - Pure form component (no layout)
 * Used by both AssumptionsSettings.mobile (with SettingsPageLayout) and SettingsModal
 *
 * Essential: retirementAge, lifeExpectancy, expectedReturnRate, inflationRate,
 *            expensesAtRetirement
 * Advanced: realEstateAppreciationRate, otherAssetGrowthRate, depreciationRate,
 *           healthcareInflationBuffer, withdrawalOrder, estimatedRetirementTaxRate,
 *           desiredLegacyAmount
 */
export function AssumptionsForm() {
  const {
    formData,
    loading,
    handleChange,
    handleBlur,
    isRetired,
    showAdvanced,
    toggleAdvanced,
  } = useAssumptionsLogic();

  // Portfolio allocation sum check
  const allocationSum = useMemo(() => {
    return new BigNumber(formData.cashEquivalentsPct ?? 0)
      .plus(formData.fixedIncomePct ?? 0)
      .plus(formData.equitiesPct ?? 0)
      .toNumber();
  }, [
    formData.cashEquivalentsPct,
    formData.fixedIncomePct,
    formData.equitiesPct,
  ]);

  const allocationValid = Math.abs(allocationSum - 1) < 0.001;

  /** Apply a portfolio preset */
  const applyPreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    handleChange("cashEquivalentsPct", values.cash);
    handleChange("fixedIncomePct", values.fixedIncome);
    handleChange("equitiesPct", values.equities);
  };

  // Get statistics help text for each field
  const retirementAgeHelp = useFieldStatistic(
    "retirementAge",
    "Target age when you plan to retire"
  );
  const lifeExpectancyHelp = useFieldStatistic(
    "lifeExpectancy",
    "Estimated age to plan your retirement horizon"
  );
  const expectedReturnRateHelp = useFieldStatistic(
    "expectedReturnRate",
    "Expected long-term annual return for your portfolio"
  );
  const inflationRateHelp = useFieldStatistic(
    "inflationRate",
    "Expected long-term annual inflation rate"
  );
  const expensesAtRetirementHelp = useFieldStatistic(
    "expensesAtRetirement",
    "Expected % change in expenses when you retire (e.g., -20% for 20% reduction)"
  );
  const realEstateAppreciationRateHelp = useFieldStatistic(
    "realEstateAppreciationRate",
    "Expected annual appreciation rate for real estate properties"
  );
  const otherAssetGrowthRateHelp = useFieldStatistic(
    "otherAssetGrowthRate",
    "Expected annual growth rate for other appreciable assets"
  );
  const depreciationRateHelp = useFieldStatistic(
    "depreciationRate",
    "Expected annual depreciation rate for vehicles, boats, equipment"
  );

  // Advanced field statistics
  const healthcareInflationBufferHelp = useFieldStatistic(
    "healthcareInflationBuffer",
    "Additional inflation buffer for healthcare costs above general inflation"
  );
  const estimatedRetirementTaxRateHelp = useFieldStatistic(
    "estimatedRetirementTaxRate",
    "Estimated average tax rate on retirement income"
  );

  if (loading) {
    return null;
  }

  return (
    <form className="settings-form-group" id="assumptions-form">
      {/* ── Core Assumptions ── */}
      <SectionHeader title="Core Assumptions" sectionId="core-assumptions" />

      <div className="form-grid">
        {/* Show Retirement Age only if user is NOT retired */}
        {!isRetired && (
          <div className="form-field">
            <AgeInput
              name="retirementAge"
              value={formData.retirementAge}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Retirement Age"
              min={18}
              helpText={retirementAgeHelp}
              suffix="yrs"
            />
          </div>
        )}

        <div className="form-field">
          <AgeInput
            name="lifeExpectancy"
            value={formData.lifeExpectancy}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Life Expectancy"
            min={18}
            helpText={lifeExpectancyHelp}
            suffix="yrs"
          />
        </div>

        <PercentageInput
          name="expectedReturnRate"
          value={formData.expectedReturnRate}
          onChange={handleChange}
          onBlur={handleBlur}
          label="Expected Return Rate"
          min={0}
          helpText={expectedReturnRateHelp}
        />

        <PercentageInput
          name="inflationRate"
          value={formData.inflationRate}
          onChange={handleChange}
          onBlur={handleBlur}
          label="Inflation Rate"
          min={0}
          helpText={inflationRateHelp}
        />

        {/* Show Expenses At Retirement only if user is NOT retired */}
        {!isRetired && (
          <PercentageChangeInput
            name="expensesAtRetirement"
            value={formData.expensesAtRetirement}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Expenses At Retirement"
            helpText={expensesAtRetirementHelp}
          />
        )}
      </div>

      {/* ── Portfolio Allocation ── */}
      <SectionHeader
        title="Portfolio Allocation"
        sectionId="portfolio-allocation"
      />

      {/* Preset selector */}
      <div className="form-preset-row" id="portfolio-presets">
        <button
          type="button"
          className="form-preset-button"
          onClick={() => applyPreset("conservative")}
        >
          Conservative
        </button>
        <button
          type="button"
          className="form-preset-button"
          onClick={() => applyPreset("moderate")}
        >
          Moderate
        </button>
        <button
          type="button"
          className="form-preset-button"
          onClick={() => applyPreset("aggressive")}
        >
          Aggressive
        </button>
      </div>

      <div className="form-grid">
        <PercentageInput
          name="cashEquivalentsPct"
          value={formData.cashEquivalentsPct}
          onChange={handleChange}
          onBlur={handleBlur}
          label="Cash & Equivalents %"
          min={0}
          max={100}
          helpText="GICs, savings accounts, money market funds"
          placeholder="5"
        />

        <PercentageInput
          name="fixedIncomePct"
          value={formData.fixedIncomePct}
          onChange={handleChange}
          onBlur={handleBlur}
          label="Fixed Income %"
          min={0}
          max={100}
          helpText="Bonds, bond ETFs"
          placeholder="35"
        />

        <PercentageInput
          name="equitiesPct"
          value={formData.equitiesPct}
          onChange={handleChange}
          onBlur={handleBlur}
          label="Equities %"
          min={0}
          max={100}
          helpText="Stocks, equity ETFs"
          placeholder="60"
        />
      </div>

      {/* Sum validation warning */}
      {!allocationValid &&
        (formData.cashEquivalentsPct != null ||
          formData.fixedIncomePct != null ||
          formData.equitiesPct != null) && (
          <p
            className="form-validation-warning"
            role="alert"
            id="allocation-warning"
          >
            Portfolio allocation sums to{" "}
            {new BigNumber(allocationSum).times(100).toFixed(1)}% — must equal
            100%
          </p>
        )}

      {/* ── Advanced Fields ── */}
      <ShowMoreToggle expanded={showAdvanced} onToggle={toggleAdvanced}>
        {/* Custom Assumptions - Gated Feature */}
        <FeatureLockedContent
          featureId="feature-custom-assumptions"
          fallback={<UpgradePrompt featureId="feature-custom-assumptions" />}
        >
          <SectionHeader
            title="Growth & Depreciation"
            sectionId="growth-depreciation"
          />

          <div className="form-grid">
            <PercentageInput
              name="realEstateAppreciationRate"
              value={formData.realEstateAppreciationRate}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Real Estate Appreciation Rate"
              min={0}
              helpText={realEstateAppreciationRateHelp}
            />

            <PercentageInput
              name="otherAssetGrowthRate"
              value={formData.otherAssetGrowthRate}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Other Appreciable Asset Growth Rate"
              min={0}
              helpText={otherAssetGrowthRateHelp}
            />

            <PercentageInput
              name="depreciationRate"
              value={formData.depreciationRate}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Depreciation Rate"
              min={0}
              helpText={depreciationRateHelp}
            />

            <PercentageInput
              name="healthcareInflationBuffer"
              value={formData.healthcareInflationBuffer}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Healthcare Inflation Buffer"
              min={0}
              max={10}
              helpText={healthcareInflationBufferHelp}
            />
          </div>

          <SectionHeader
            title="Per-Class Return Rates"
            sectionId="per-class-returns"
          />

          <div className="form-grid">
            <PercentageInput
              name="cashEquivalentsReturn"
              value={formData.cashEquivalentsReturn}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Cash & Equivalents Return"
              min={0}
              max={20}
              helpText="Expected annual return for cash and GICs"
              placeholder="2.0"
            />

            <PercentageInput
              name="fixedIncomeReturn"
              value={formData.fixedIncomeReturn}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Fixed Income Return"
              min={0}
              max={20}
              helpText="Expected annual return for bonds and fixed income"
              placeholder="4.0"
            />

            <PercentageInput
              name="equitiesReturn"
              value={formData.equitiesReturn}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Equities Return"
              min={0}
              max={30}
              helpText="Expected annual return for stocks and equity ETFs"
              placeholder="8.0"
            />
          </div>

          <SectionHeader title="Tax & Legacy" sectionId="tax-legacy" />

          <div className="form-grid">
            <WithdrawalOrderSelect
              value={
                typeof formData.withdrawalOrder === "string"
                  ? formData.withdrawalOrder
                  : ""
              }
              onChange={(value) =>
                handleChange("withdrawalOrder", value as unknown as number)
              }
            />

            <PercentageInput
              name="estimatedRetirementTaxRate"
              value={formData.estimatedRetirementTaxRate}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Estimated Retirement Tax Rate"
              min={0}
              max={60}
              helpText={estimatedRetirementTaxRateHelp}
            />

            <CurrencyInput
              name="desiredLegacyAmount"
              value={formData.desiredLegacyAmount}
              onChange={(name, value) =>
                handleChange(name, value as unknown as number)
              }
              onBlur={(name, value) =>
                handleBlur(name, value as unknown as number)
              }
              label="Desired Legacy Amount"
              helpText="Target amount to leave as inheritance"
            />
          </div>
        </FeatureLockedContent>
      </ShowMoreToggle>
    </form>
  );
}
